import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import Card from 'components/card';
import { useSearchParams } from 'react-router-dom';
import GoBack from 'components/actions/GoBack';
import { MdContentCopy, MdCode, MdLink, MdCheckCircle } from 'react-icons/md';
import { toast } from 'react-toastify';

const IntegrationHelp = () => {
    const [searchParams] = useSearchParams();
    const offerId = searchParams.get('offerId');
    const initialTemplateId = searchParams.get('templateId');
    
    const [user, setUser] = useState<any>(null);
    const [offer, setOffer] = useState<any>({});
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialTemplateId || '');
    const [activeTab, setActiveTab] = useState<'iframe' | 'link'>('iframe');

    // Generate form link for integration
    const formLink = offer?._id && selectedTemplateId
        ? `${window.location.origin}/public/lead-form?offerId=${offer._id}&sellerId=${user?._id}&templateId=${selectedTemplateId}`
        : '';

    const iframeCode = formLink
        ? `<iframe src="${formLink}" style="width:100%;min-height:600px;border:none"></iframe>`
        : '';

    useEffect(() => {
        if (
            !localStorage.getItem('token') ||
            localStorage.getItem('token').length < 10
        ) {
            window.location.href = '/auth/sign-up';
        }
        if (offerId) {
            getOffer(offerId);
        }
        initUser();
        fetchTemplates();
        // eslint-disable-next-line
    }, [offerId]);

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                setUser(res.data);
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    const getOffer = (id: string) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/offer/${id}`, config)
            .then((res) => {
                setOffer(res.data);
            })
            .catch((err) => {
                console.log('Error fetching offer', err);
            });
    };

    const fetchTemplates = async () => {
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };
            const response = await axios.get(`${CONFIG.apiUrl}/form-template`, config);
            setTemplates(response.data);
            
            // Set default template if not already set
            if (!selectedTemplateId && response.data.length > 0) {
                const defaultTemplate = response.data.find((t: any) => t.isDefault);
                setSelectedTemplateId(defaultTemplate?._id || response.data[0]._id);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copié !`);
    };

    return (
        <>
            <GoBack />
            <div className="mt-3 h-full w-full rounded-[20px]">
                {/* Header */}
                <Card extra="w-full pb-6 pt-6 px-8 mb-5 shadow-3xl rounded-[20px] border">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-navy-700 dark:text-white mb-2">
                                Intégration du formulaire
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Intégrez facilement votre formulaire de capture de leads sur votre site web
                            </p>
                        </div>
                    </div>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Left Column - Configuration & Code */}
                    <div className="space-y-5">
                        {/* Template Selection */}
                        <Card extra="p-6 shadow-3xl rounded-[20px] border">
                            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
                                1. Choisir un template
                            </h2>
                            {templates.length > 0 ? (
                                <select
                                    className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-leadrs focus:outline-none dark:bg-navy-900 dark:border-navy-700 dark:text-white"
                                    value={selectedTemplateId}
                                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                                >
                                    {templates.map((t) => (
                                        <option key={t._id} value={t._id}>
                                            {t.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                                        Aucun template disponible. Vous devez d'abord créer un template de formulaire.
                                    </p>
                                    <a
                                        href="/admin/form-templates"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-leadrs text-white rounded-lg text-sm font-medium hover:bg-leadrsLight transition-colors"
                                    >
                                        Créer un template
                                    </a>
                                </div>
                            )}
                        </Card>

                        {/* Integration Methods */}
                        <Card extra="p-6 shadow-3xl rounded-[20px] border">
                            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
                                2. Méthode d'intégration
                            </h2>
                            
                            {/* Tabs */}
                            <div className="flex gap-2 mb-6">
                                <button
                                    onClick={() => setActiveTab('iframe')}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                        activeTab === 'iframe'
                                            ? 'bg-leadrs text-white'
                                            : 'bg-lightPrimary text-gray-700 hover:bg-gray-200 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700'
                                    }`}
                                >
                                    <MdCode size={16} />
                                    Iframe (Recommandé)
                                </button>
                                <button
                                    onClick={() => setActiveTab('link')}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                        activeTab === 'link'
                                            ? 'bg-leadrs text-white'
                                            : 'bg-lightPrimary text-gray-700 hover:bg-gray-200 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-navy-700'
                                    }`}
                                >
                                    <MdLink size={16} />
                                    Lien direct
                                </button>
                            </div>

                            {/* Iframe Tab */}
                            {activeTab === 'iframe' && formLink && (
                                <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                        <h3 className="font-bold text-navy-700 dark:text-white mb-2 flex items-center gap-2">
                                            <MdCheckCircle className="text-blue-500" />
                                            Comment ça marche ?
                                        </h3>
                                        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-2">
                                            <li>Copiez le code iframe ci-dessous</li>
                                            <li>Collez-le dans le HTML de votre page</li>
                                            <li>Le formulaire s'affiche automatiquement</li>
                                        </ol>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-bold mb-2 text-navy-700 dark:text-white">
                                            Code à copier
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                value={iframeCode}
                                                readOnly
                                                rows={4}
                                                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-xs bg-gray-50 dark:bg-navy-900 dark:border-navy-700 dark:text-white font-mono pr-24"
                                            />
                                            <button
                                                className="absolute right-2 top-2 linear flex items-center gap-1.5 rounded-lg bg-leadrs text-white px-2.5 py-1.5 text-xs transition duration-200 hover:bg-leadrsLight"
                                                onClick={() => copyToClipboard(iframeCode, 'Code iframe')}
                                                type="button"
                                            >
                                                <MdContentCopy size={14} />
                                                Copier
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Link Tab */}
                            {activeTab === 'link' && formLink && (
                                <div className="space-y-4">
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                        <h3 className="font-bold text-navy-700 dark:text-white mb-2 flex items-center gap-2">
                                            <MdCheckCircle className="text-green-500" />
                                            Comment ça marche ?
                                        </h3>
                                        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-2">
                                            <li>Copiez l'URL ci-dessous</li>
                                            <li>Utilisez-la dans un bouton ou menu</li>
                                            <li>Les visiteurs accèdent au formulaire</li>
                                        </ol>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-bold mb-2 text-navy-700 dark:text-white">
                                            URL du formulaire
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={formLink}
                                                readOnly
                                                className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm bg-gray-50 dark:bg-navy-900 dark:border-navy-700 dark:text-white"
                                            />
                                            <button
                                                className="linear flex items-center gap-1.5 rounded-lg bg-leadrs text-white px-3 py-2 text-sm transition duration-200 hover:bg-leadrsLight whitespace-nowrap"
                                                onClick={() => copyToClipboard(formLink, 'Lien')}
                                                type="button"
                                            >
                                                <MdContentCopy size={16} />
                                                Copier
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </div>

                    {/* Right Column - Preview */}
                    <div>
                        <Card extra="p-6 shadow-3xl rounded-[20px] border h-full">
                            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
                                Aperçu du formulaire
                            </h2>
                            {formLink ? (
                                <div className="rounded-lg border-2 border-gray-200 dark:border-navy-700 overflow-hidden bg-white shadow-sm">
                                    <iframe
                                        src={formLink}
                                        title="Formulaire de lead"
                                        style={{ width: '100%', height: '500px', border: 'none' }}
                                    />
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-[500px] rounded-lg border-2 border-dashed border-gray-300 dark:border-navy-700">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Sélectionnez un template pour voir l'aperçu
                                    </p>
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
            </div>
        </>
    );
};

export default IntegrationHelp;
